/**
 * Publishing (spec §20-21). Provider-neutral publisher adapters, the same
 * posture as generation: the domain knows "publish this asset to this
 * platform", and an adapter knows the Graph API. Publishing ships
 * incrementally (Website Hero first, since HipNumber owns the storefront -
 * spec §70), and the existing Meta connection work is reused, not duplicated
 * (spec §20).
 *
 * The one hard rule encoded here is idempotency (spec §21): a publish job key
 * is `publish:<assetId>:<platform>:<version>`, so a worker retry can never
 * create a duplicate social post.
 */

import { z } from 'zod';

export const MARKETING_PLATFORMS = ['FACEBOOK', 'INSTAGRAM', 'YOUTUBE', 'TIKTOK', 'EMAIL', 'SMS', 'WEBSITE'] as const;
export const MarketingPlatformSchema = z.enum(MARKETING_PLATFORMS);
export type MarketingPlatform = z.infer<typeof MarketingPlatformSchema>;

export interface PublisherCapabilities {
  canPublishNow: boolean;
  canSchedule: boolean;
  canDeleteScheduled: boolean;
  supportsVideo: boolean;
  supportsImage: boolean;
}

export interface ValidationResult {
  ok: boolean;
  /** An actionable message when not ok (spec §78), never a raw provider error. */
  message?: string;
}

export interface PublishResult {
  ok: boolean;
  /** The external post/permalink id, for later analytics joins. */
  externalPostId?: string;
  message?: string;
}

export interface ScheduleResult {
  ok: boolean;
  externalScheduleId?: string;
  message?: string;
}

export interface PublisherConnectionInput {
  connectionId: string;
}
export interface PublishAssetInput {
  assetId: string;
  storageKey: string;
  caption?: string;
  destinationUrl?: string;
}
export interface ScheduleAssetInput extends PublishAssetInput {
  scheduledAtUtc: string;
}
export interface DeleteScheduledInput {
  externalScheduleId: string;
}

export interface MarketingPublisher {
  readonly platform: MarketingPlatform;
  validateConnection(input: PublisherConnectionInput): Promise<ValidationResult>;
  publish(input: PublishAssetInput): Promise<PublishResult>;
  schedule?(input: ScheduleAssetInput): Promise<ScheduleResult>;
  deleteScheduled?(input: DeleteScheduledInput): Promise<void>;
  capabilities(): PublisherCapabilities;
}

/**
 * The idempotency key a publish job MUST carry (spec §21). Pinning the asset
 * VERSION means a regenerated asset republishes as a distinct post, while a
 * retry of the same version is dropped by the worker.
 */
export function publishIdempotencyKey(input: { assetId: string; platform: MarketingPlatform; version: number }): string {
  return `publish:${input.assetId}:${input.platform}:${input.version}`;
}

/** The destination-identity confirmation shown before a first direct publish
 *  (spec §21). Publishing is a permissioned, confirmed act - never silent. */
export interface PublishConfirmation {
  platform: MarketingPlatform;
  destinationName: string;
  assetName: string;
  caption?: string;
}
